import { computed, makeObservable } from 'mobx'

import { SitePermitStatus } from '~/client/graph'
import MobileEventsStore from '~/client/src/mobile/stores/EventStore/MobileEvents.store'
import { UNASSIGNED_FILTER_OPTION } from '~/client/src/shared/components/Deliveries/DeliveriesView.store'
import { getAttachmentFileType } from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import { AttachmentFileTypeList } from '~/client/src/shared/enums/FileType'
import {
  formsFilterTypes,
  getLogisticsFilterTypeCaption,
  LogisticsFilterType,
} from '~/client/src/shared/enums/LogisticsFilterType'
import UserProject from '~/client/src/shared/models/UserProject'
import BaseLogisticsFilterStore, {
  ISourceMap,
} from '~/client/src/shared/stores/BaseLogisticsFilter.store'
import FormCategoriesStore from '~/client/src/shared/stores/domain/FormCategories.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import PermitTypesStore from '~/client/src/shared/stores/domain/PermitTypes.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import LogisticsFilterStore from '~/client/src/shared/stores/LogisticsFilter.store'
import { enumToList } from '~/client/src/shared/utils/converters'
import { LOCATION_SEPARATOR } from '~/client/src/shared/utils/usefulStrings'

import MobileLogisticsStore from './MobileLogistics.store'
import MobileLogisticsListStore from './MobileLogisticsList.store'

export default class MobileLogisticsFilterStore extends LogisticsFilterStore {
  public constructor(
    protected readonly eventsStore: MobileEventsStore,
    protected readonly logisticsStore: MobileLogisticsStore,
    protected readonly logisticsListStore: MobileLogisticsListStore,
    protected readonly onShowChanged: (
      isShown: boolean,
      filterType: string,
    ) => void,
    protected readonly locationAttributesStore: LocationAttributesStore,
    protected readonly permitTypesStore: PermitTypesStore,
    protected readonly tagsStore: TagsStore,
    protected readonly formCategoriesStore: FormCategoriesStore,
    protected readonly userProjectsStore: UserProjectsStore,
    protected readonly projectsStore: ProjectsStore,
    protected readonly sharedSitePermitStore: SharedSitePermitStore,
  ) {
    super(
      eventsStore,
      logisticsStore,
      onShowChanged,
      locationAttributesStore,
      permitTypesStore,
      tagsStore,
      formCategoriesStore,
      projectsStore,
      sharedSitePermitStore,
    )
    makeObservable(this)
  }

  protected get availableInstances() {
    return this.logisticsStore.logisticItemsInPeriodInterval
  }

  @computed
  public get filterStoresByTypeMap(): {
    [filterType: string]: BaseLogisticsFilterStore
  } {
    const { appState } = this.eventsStore
    const map: { [filterType: string]: BaseLogisticsFilterStore } = {}

    this.filterTypes.forEach(filterType => {
      map[filterType] = new BaseLogisticsFilterStore(
        filterType,
        appState,
        () => this.sourceMapByFilterTypeMap[filterType],
        this.logisticsListStore,
        this.onShowChanged,
        this.fieldsMap(),
        getLogisticsFilterTypeCaption,
        this.getOptionName,
      )
    })

    return map
  }

  @computed
  protected get sourceMapByFilterTypeMap(): {
    [filterType: string]: ISourceMap
  } {
    const maps = this.filterTypes.reduce((acc, filterType) => {
      acc[filterType] = this.getDefaultSourceMapByType(filterType)
      return acc
    }, {})

    this.availableInstances.forEach(instance => {
      this.filterTypes.forEach(filterType => {
        const map = maps[filterType]
        let optionIds = []

        switch (filterType) {
          case LogisticsFilterType.Status:
            optionIds = [instance.status || UNASSIGNED_FILTER_OPTION]
            break
          case LogisticsFilterType.Location:
            optionIds = this.getLocationValues(instance.locations)
            break
          case LogisticsFilterType.Equipment:
            optionIds = this.getLocationValues(instance.equipment)
            break
          case LogisticsFilterType.Company:
            optionIds = instance.companyIds?.length
              ? instance.companyIds
              : [UNASSIGNED_FILTER_OPTION]
            break
          case LogisticsFilterType.ResponsibleContact:
            optionIds = instance.responsibleContactIds?.length
              ? instance.responsibleContactIds
              : [UNASSIGNED_FILTER_OPTION]
            break
          case LogisticsFilterType.Project:
            const sharedSitePermit = this.sharedSitePermitStore.getById(
              instance.entityId,
            )
            optionIds = [
              sharedSitePermit?.projectFromId ||
                this.eventsStore.appState.activeProject.id ||
                UNASSIGNED_FILTER_OPTION,
            ]
            break
          case LogisticsFilterType.Attachment:
            const attachmentTypes = instance.attachments?.length
              ? instance.attachments.map(a => getAttachmentFileType(a.fileName))
              : [UNASSIGNED_FILTER_OPTION]
            optionIds = Array.from(new Set(attachmentTypes))
            break
          case LogisticsFilterType.Type: {
            const form = this.logisticsStore.getFormById(instance.entityId)
            optionIds = [
              form?.getTypeOfPermitType(this.permitTypesStore) ||
                UNASSIGNED_FILTER_OPTION,
            ]
            break
          }
          case LogisticsFilterType.Category: {
            const form = this.logisticsStore.getFormById(instance.entityId)
            const permitType = this.permitTypesStore.getPermitTypeById(
              form?.typeId,
            )
            optionIds = [permitType?.categoryId || UNASSIGNED_FILTER_OPTION]
            break
          }
        }

        optionIds.forEach(optionId => {
          if (!map[optionId]) {
            optionId = UNASSIGNED_FILTER_OPTION
          }

          map[optionId]?.push(instance.id)
        })
      })
    })

    const userId = this.eventsStore.appState.user.getId()
    const myCompanyId = UserProject.getCompanyId(
      this.eventsStore.appState.user,
      this.userProjectsStore,
    )

    if (!maps[LogisticsFilterType.ResponsibleContact][userId])
      maps[LogisticsFilterType.ResponsibleContact][userId] = []

    if (!maps[LogisticsFilterType.Company][myCompanyId])
      maps[LogisticsFilterType.Company][myCompanyId] = []

    return maps
  }

  protected getDefaultSourceMapByType(type: LogisticsFilterType) {
    let sourceList: string[] = []

    switch (type) {
      case LogisticsFilterType.Status:
        sourceList = [...enumToList(SitePermitStatus)]
        sourceList.push(UNASSIGNED_FILTER_OPTION)
        break
      case LogisticsFilterType.ResponsibleContact:
        sourceList = this.availableInstances.reduce((acc, instance) => {
          instance.responsibleContactIds?.forEach(id => {
            if (!acc.includes(id)) {
              acc.push(id)
            }
          })
          return acc
        }, [])
        break
      case LogisticsFilterType.Location:
        sourceList = [
          ...this.locationAttributesStore.allLocationsWithoutEquipment.map(
            l => `${l.type}${LOCATION_SEPARATOR}${l.id}`,
          ),
          UNASSIGNED_FILTER_OPTION,
        ]
        break
      case LogisticsFilterType.Equipment:
        sourceList = [
          ...this.locationAttributesStore.offloadingEquipmentsStore.list.map(
            l => `${l.type}${LOCATION_SEPARATOR}${l.id}`,
          ),
          UNASSIGNED_FILTER_OPTION,
        ]
        break
      case LogisticsFilterType.Company:
        sourceList = this.availableInstances.reduce((acc, instance) => {
          instance.companyIds?.forEach(id => {
            if (!acc.includes(id)) {
              acc.push(id)
            }
          })
          return acc
        }, [])
        break
      case LogisticsFilterType.Type:
        sourceList = this.availableInstances.map(instance => {
          return instance.type || UNASSIGNED_FILTER_OPTION
        })
        break
      case LogisticsFilterType.Category:
        sourceList = this.availableInstances.map(instance => {
          const form = this.logisticsStore.getFormById(instance.entityId)
          if (!form?.typeId) return UNASSIGNED_FILTER_OPTION
          const permitType = this.permitTypesStore.getPermitTypeById(
            form.typeId,
          )
          return permitType?.categoryId || UNASSIGNED_FILTER_OPTION
        })
        break
      case LogisticsFilterType.Attachment:
        sourceList = [...AttachmentFileTypeList, UNASSIGNED_FILTER_OPTION]
        break
      case LogisticsFilterType.Project:
        const projectsSet = new Set<string>()
        this.availableInstances.forEach(instance => {
          const sharedSitePermit = this.sharedSitePermitStore.getById(
            instance.entityId,
          )
          if (sharedSitePermit) {
            projectsSet.add(sharedSitePermit.projectFromId)
          } else {
            projectsSet.add(this.eventsStore.appState.activeProject.id)
          }
        })
        sourceList = Array.from(projectsSet)
        break
    }
    return sourceList.reduce((acc, optionId) => {
      acc[optionId] = []
      return acc
    }, {})
  }

  protected fieldsMap() {
    return this.eventsStore.appState.formsFilters.fieldsMap
  }

  protected get filterTypes(): LogisticsFilterType[] {
    return formsFilterTypes
  }
}
